import { createFileRoute, notFound } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import { useEndTurnConfirmation } from "#/components/game/useEndTurnConfirmation";
import { PlayMapAccessGate } from "#/components/play/PlayMapAccessUpsell";
import { PlayMapPageShell } from "#/components/play/PlayMapPageShell";
import {
  shouldSkipTutorialPrompt,
  TutorialPromptDialog,
} from "#/components/play/TutorialPromptDialog";
import {
  useAiPlaybackStepTimer,
  useEscapeCancelsSelection,
  usePlayMapBoardChrome,
  usePlayMapGameHotkeys,
  usePlayMapTurnActions,
  useVictoryAutoSubmitTurn,
} from "#/components/play/usePlayMapSession";
import { BoardImagesGate } from "#/game/BoardImagesGate";
import { GameStoreProvider, useGameSelector } from "#/game/GameStoreProvider";
import { readAnonymousPlayerId, syncAnonymousPlayerIdOnClient } from "#/lib/anonymousPlayerId";
import {
  mapOgImageUrl,
  playMapDescription,
  playMapRobots,
  playMapTitle,
} from "#/lib/documentTitle";
import { isMapAccessUpsellReason } from "#/lib/mapAccessUpsellCopy";
import { mapIdRouteHead, mapIdRouteParams } from "#/lib/routeParams";
import { useRequestGameContext } from "#/lib/useRequestGameContext";
import {
  loadPlayMapPage,
  type PlayMapPageReady,
  type PlayMapPageTutorialPrompt,
} from "#/server/game/playMapPage";
import { startMapRunFn } from "#/server/game/writeHandlers";

function readPlayerId(): string | undefined {
  return readAnonymousPlayerId();
}

export const Route = createFileRoute("/play_/$mapId")({
  params: mapIdRouteParams,
  loader: async ({ params: { mapId } }) => {
    const playerId = readPlayerId();
    const result = await loadPlayMapPage({
      data: playerId ? { mapId, playerId } : { mapId },
    });
    if (
      result.status === "access-denied" &&
      (result.reason === "invalid-id" || result.reason === "not-released")
    ) {
      throw notFound();
    }
    if (playerId) {
      syncAnonymousPlayerIdOnClient({
        session: { playerId, isAnonymous: true },
      });
    }
    return result;
  },
  head: ({ params: { mapId } }) =>
    mapIdRouteHead({
      mapId,
      titleForMapId: playMapTitle,
      descriptionForMapId: playMapDescription,
      pathForMapId: (validMapId) => `/play/${validMapId}`,
      imageUrlForMapId: mapOgImageUrl,
      robotsForMapId: playMapRobots,
    }),
  component: PlayPage,
});

function PlayPage() {
  const loaderData = Route.useLoaderData();

  if (loaderData.status === "access-denied") {
    if (!isMapAccessUpsellReason(loaderData.reason)) {
      throw notFound();
    }
    return <PlayMapAccessGate reason={loaderData.reason} mapId={loaderData.mapId} />;
  }

  if (loaderData.status === "tutorial-prompt") {
    return <PlayMapTutorialPromptPage initial={loaderData} />;
  }

  return <PlayMapReadyPage initial={loaderData} />;
}

function PlayMapTutorialPromptPage({ initial }: { initial: PlayMapPageTutorialPrompt }) {
  const skipPrompt = shouldSkipTutorialPrompt();
  const [ready, setReady] = useState<PlayMapPageReady | null>(null);

  useEffect(() => {
    if (!skipPrompt || ready) return;
    let cancelled = false;
    void startMapRunFn({ data: { mapId: initial.mapId, playerId: initial.playerId } }).then(
      (result) => {
        if (cancelled) return;
        setReady({ status: "ready", ...result });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [skipPrompt, initial.mapId, initial.playerId, ready]);

  const handleStarted = useCallback((next: PlayMapPageReady) => {
    setReady(next);
  }, []);

  if (ready) {
    return <PlayMapReadyPage initial={ready} />;
  }

  if (skipPrompt) {
    return null;
  }

  return (
    <TutorialPromptDialog
      mapId={initial.mapId}
      playerId={initial.playerId}
      onStarted={handleStarted}
    />
  );
}

function PlayMapReadyPage({ initial }: { initial: PlayMapPageReady }) {
  const { fastAnimations, endTurnConfirmation, disableAiPlayback } = useRequestGameContext();
  return (
    <BoardImagesGate state={initial.state} mapId={initial.mapId}>
      <GameStoreProvider
        initialState={initial.state}
        initialServerDayNumber={initial.currentDay}
        settings={{ fastPlayback: fastAnimations, endTurnConfirmation, disableAiPlayback }}
      >
        <PlayMapSession initial={initial} />
      </GameStoreProvider>
    </BoardImagesGate>
  );
}

function PlayMapSession({ initial }: { initial: PlayMapPageReady }) {
  const { mapId: routeMapId } = Route.useParams();

  useAiPlaybackStepTimer();
  useEscapeCancelsSelection();
  useVictoryAutoSubmitTurn({ playerId: initial.playerId, mapId: initial.mapId });
  const endTurnConfirmation = useGameSelector((s) => s.endTurnConfirmation);
  const isSubmitting = useGameSelector((s) => s.isSubmitting);
  const { handleEndTurn, handleRestart, isRestarting } = usePlayMapTurnActions({
    playerId: initial.playerId,
    mapId: initial.mapId,
    routeMapId,
  });
  const { requestEndTurn, confirmDialog } = useEndTurnConfirmation({
    onConfirm: handleEndTurn,
    pending: isSubmitting,
    confirmBeforeEndTurn: endTurnConfirmation,
  });

  usePlayMapGameHotkeys({ onEndTurn: requestEndTurn });

  const chrome = usePlayMapBoardChrome();

  return (
    <>
      <PlayMapPageShell
        handleEndTurn={requestEndTurn}
        handleRestart={handleRestart}
        isRestarting={isRestarting}
        medalThresholds={initial.medalThresholds}
        {...chrome}
      />
      {confirmDialog}
    </>
  );
}
